import { atom } from "jotai";
import PropTypes from "prop-types";
import * as React from "react";
import {
  actionCreators,
  useCurrentUser,
  useStateValue,
} from "../../../globalState";
import { Button, Chip, Dialog, Typography } from "../../../themeV2";
import AccessList from "../../Dashboard/AdvancedSettings/AccessList";
import ChangeShopOwner from "../../Dashboard/AdvancedSettings/ChangeShopOwner";
import CharacterAssociations from "../../Dashboard/AdvancedSettings/CharacterAssociations";
import CharityField from "../../Dashboard/AdvancedSettings/CharityField";
import FranchiseAssociation from "../../Dashboard/AdvancedSettings/FranchiseAssociation";
import GroupShopSection from "../../Dashboard/AdvancedSettings/GroupShopSection";
import ServiceBumpSection from "../../Dashboard/AdvancedSettings/ServiceBumpSection";
import SettingsSection, {
  Pair,
} from "../../Dashboard/AdvancedSettings/SettingsSection";
import ShopToggles from "../../Dashboard/AdvancedSettings/ShopToggles";
import { updateInfluencer } from "../../Dashboard/AdvancedSettings/service";
import ShopSplit from "../../Dashboard/InfluencerModal/ShopSplit";
import styles from "./ShopSettingsDialog.module.css";

const userInviteModalAtom = atom(false);

const AdvancedSettingsContent = React.memo(function AdvancedSettingsContent({
  influencer,
  onClose,
}) {
  const [, dispatch] = useStateValue();
  const currentUser = useCurrentUser();
  const { isAdmin } = currentUser;

  const [charityName, setCharityName] = React.useState(
    influencer.charity_name || "",
  );
  const [shopHashTag, setShopHashTag] = React.useState(
    influencer.product_tag_string || "",
  );

  const [isEditingServiceFeeBump, setIsEditingServiceFeeBump] =
    React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [shopOwnerError, setShopOwnerError] = React.useState("");
  const [accessListError, setAccessListError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = React.useState(false);

  const unsaved = React.useMemo(
    () =>
      (charityName || "") !== (influencer.charity_name || "") ||
      (shopHashTag || "") !== (influencer.product_tag_string || ""),
    [charityName, shopHashTag, influencer],
  );

  const triggerAlert = React.useCallback(
    (alert) => dispatch(actionCreators.createTriggerAlert(alert)),
    [dispatch],
  );

  const handleDiscard = React.useCallback(() => {
    setShowDiscardConfirm(false);
    onClose();
  }, [onClose]);

  const clearErrors = React.useCallback(() => {
    setErrorText("");
  }, []);

  const handleEditServiceFeeBump = React.useCallback((isEditing) => {
    setIsEditingServiceFeeBump(isEditing);
    if (!isEditing) setErrorText("");
  }, []);

  const handleSubmit = async () => {
    if (isLoading) return;

    if (isEditingServiceFeeBump) {
      setErrorText(
        'Please save service fee bump before clicking "Save". Changes are still unsaved.',
      );
      return;
    }

    clearErrors();
    setIsLoading(true);

    try {
      const product_tag_string =
        isAdmin && Boolean(influencer.group_shop)
          ? !shopHashTag
            ? null
            : shopHashTag
          : undefined;

      await updateInfluencer(influencer.influencer_id, {
        charity_name: charityName?.trim?.(),
        product_tag_string,
      });

      triggerAlert({ message: "Saved" });
      onClose();
    } catch (error) {
      console.error(error);
      setErrorText(error.message || "Something went wrong. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div>
      {isAdmin && (
        <Pair>
          <SettingsSection title="Shop Owner" error={shopOwnerError}>
            <ChangeShopOwner
              influencerId={influencer.influencer_id}
              triggerAlert={triggerAlert}
              setErrorText={setShopOwnerError}
            />
          </SettingsSection>
          <SettingsSection title="Payout Account">
            <ShopSplit
              influencerId={influencer.influencer_id}
              userInviteModalAtom={userInviteModalAtom}
            />
          </SettingsSection>
        </Pair>
      )}

      {isAdmin && (
        <Pair>
          <SettingsSection title="Service Fee">
            <ServiceBumpSection
              influencer={influencer}
              setIsEditingServiceFeeBump={handleEditServiceFeeBump}
            />
          </SettingsSection>
          <SettingsSection title="Charity">
            <CharityField
              value={charityName}
              onChange={(val) => {
                setCharityName(val);
                clearErrors();
              }}
            />
          </SettingsSection>
        </Pair>
      )}

      {isAdmin && (
        <SettingsSection title="Access List" error={accessListError}>
          <AccessList
            influencer={influencer}
            triggerAlert={triggerAlert}
            setErrorText={setAccessListError}
            userInviteModalAtom={userInviteModalAtom}
          />
        </SettingsSection>
      )}

      {isAdmin && (
        <SettingsSection title="Shop Attributes">
          <ShopToggles influencer={influencer} />
        </SettingsSection>
      )}

      {isAdmin && (
        <SettingsSection title="Collection Settings">
          <GroupShopSection
            influencerId={influencer.influencer_id}
            isGroupShop={Boolean(influencer.group_shop)}
            shopHashTag={shopHashTag}
            setShopHashTag={setShopHashTag}
            onInput={clearErrors}
          />
        </SettingsSection>
      )}

      {isAdmin && !influencer.group_shop && (
        <SettingsSection
          title="Character Associations"
          description="Associate this talent with franchise characters"
        >
          <CharacterAssociations
            influencerId={influencer.influencer_id}
            triggerAlert={triggerAlert}
          />
        </SettingsSection>
      )}

      {isAdmin && Boolean(influencer.group_shop) && (
        <SettingsSection
          title="Franchise Association"
          description="Associate this collection with a franchise"
        >
          <FranchiseAssociation
            influencerId={influencer.influencer_id}
            currentFranchiseId={influencer.franchise_id}
            currentFranchiseName={influencer.franchise_name}
            triggerAlert={triggerAlert}
          />
        </SettingsSection>
      )}

      <div className={styles.footer}>
        <div className={styles.footerStatus}>
          {unsaved && <Chip label="Unsaved" size="sm" aria-live="polite" />}
          {errorText && (
            <Typography variant="label-md" color="error" as="span">
              {errorText}
            </Typography>
          )}
        </div>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          loading={isLoading}
          style={{ minWidth: 120 }}
        >
          Save
        </Button>
      </div>

      <Dialog.Root
        alert
        open={showDiscardConfirm}
        onOpenChange={(open) => {
          if (!open) setShowDiscardConfirm(false);
        }}
      >
        <Dialog.Popup size="sm">
          <Dialog.Title>Unsaved changes</Dialog.Title>
          <Dialog.Description>
            You have unsaved changes. Discard?
          </Dialog.Description>
          <div className={styles.discardActions}>
            <Button
              variant="outlined"
              onClick={() => setShowDiscardConfirm(false)}
            >
              Keep Editing
            </Button>
            <Button onClick={handleDiscard}>Discard</Button>
          </div>
        </Dialog.Popup>
      </Dialog.Root>
    </div>
  );
});

AdvancedSettingsContent.propTypes = {
  influencer: PropTypes.shape({
    influencer_id: PropTypes.number.isRequired,
    charity_name: PropTypes.string,
    product_tag_string: PropTypes.string,
    group_shop: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    service_fee_bump: PropTypes.number,
    franchise_id: PropTypes.number,
    franchise_name: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdvancedSettingsContent;
